/**
 * f14.3: pure presentation for step 7 "Actuals". Formatting and class mapping
 * only -- whether a batch may be committed, discarded or accepted is always
 * the server's answer (`capabilities`, `acceptance_blockers`).
 */

import type { ActualsReview, ActualsStepBatch, ActualsStepExpected } from '@/api/client';

import { formatDayMonth } from '../../workflowView';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-07` -> `Jul 2026`; null -> null. */
export function periodLabel(period: string | null | undefined): string | null {
  if (!period) return null;
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;
  return `${MONTHS[Number(match[2]) - 1] ?? match[2]} ${match[1]}`;
}

export interface FeedBadge {
  className: string;
  label: string;
}

/** "Committed · 24 Sep" or "Pending" for one expected currency (design D6). */
export function feedBadge(feed: ActualsStepExpected): FeedBadge {
  if (feed.current_batch_id && feed.committed_at) {
    return { className: 'badge pill-success', label: `Committed · ${formatDayMonth(feed.committed_at)}` };
  }
  return { className: 'badge pill-warning', label: 'Pending' };
}

/** `badge pill-*` for an ingestion batch state. */
export function batchStatePill(code: string): string {
  switch (code) {
    case 'COMMITTED':
      return 'badge pill-success';
    case 'STAGED':
      return 'badge pill-info';
    case 'REJECTED':
    case 'FAILED':
      return 'badge pill-danger';
    case 'DISCARDED':
      return 'badge pill-neutral';
    default:
      return 'badge pill-pending';
  }
}

export function batchStateLabel(batch: Pick<ActualsStepBatch, 'state' | 'is_current'>): string {
  if (batch.state.code === 'COMMITTED') return batch.is_current ? 'Committed — current' : 'Committed — superseded';
  if (batch.state.code === 'STAGED') return 'Staged — not committed';
  return batch.state.name;
}

/** "400 rows · USD · Jul 2026 · variances will be raised against cycle 2607". */
export function commitSummary(review: ActualsReview, cycleCode: string): string {
  const rows = Number(review.figures['staged_rows'] ?? 0);
  const currency = review.import?.currency_code ?? '—';
  const period = periodLabel(review.import?.period_month) ?? '—';
  return `${rows} rows · ${currency} · ${period} · variances will be raised against cycle ${cycleCode}`;
}

/** The f14.2 queue, narrowed to one batch's variances (design D6/D8). */
export function queueLink(cycleId: string, batchId: string, status: string = 'PENDING'): string {
  const params = new URLSearchParams({ batch_id: batchId, status });
  return `/cycles/${encodeURIComponent(cycleId)}/reconciliation?${params.toString()}`;
}

/** The pending-variance blocker the frame shows is followed by a queue link (design D6). */
export function isPendingVarianceBlocker(message: string): boolean {
  return /reconciliation variances? from this step (is|are) still pending/.test(message);
}

export interface MatchTab {
  status: string;
  label: string;
  hint: string;
}

/** "Did not match" tabs, in order (design D6). */
export const MATCH_TABS: readonly MatchTab[] = [
  { status: 'ELEMENT_UNKNOWN', label: 'Element unknown', hint: 'Stored without an element' },
  { status: 'ENGAGEMENT_UNKNOWN', label: 'Engagement unknown', hint: 'Excluded from commit' },
  { status: 'UNIT_UNKNOWN', label: 'Unit unknown', hint: 'Excluded from commit' },
];

export function matchCount(review: ActualsReview, status: string): number {
  const counts = review.figures['by_match_status'] as Record<string, number> | undefined;
  return Number(counts?.[status] ?? 0);
}

export interface ProcessingFact {
  label: string;
  value: number | null;
}

const LEVEL_LABELS: Readonly<Record<string, string>> = {
  grand: 'Grand-total subtotal rows dropped',
  unit: 'Unit subtotal rows dropped',
  client: 'Client subtotal rows dropped',
  engagement: 'Engagement subtotal rows dropped',
};

/** "What the system did with it": the server's counters, labelled. */
export function processingFacts(review: ActualsReview): ProcessingFact[] {
  const figures = review.figures;
  const dropped = (figures['subtotals_dropped'] ?? {}) as Record<string, number>;
  const facts: ProcessingFact[] = Object.keys(LEVEL_LABELS).map((level) => ({
    label: LEVEL_LABELS[level] as string,
    value: dropped[level] ?? 0,
  }));
  const num = (key: string): number | null => {
    const value = figures[key];
    return value == null ? null : Number(value);
  };
  facts.push({ label: 'Rows without Total Revenue', value: num('no_total_revenue_rows') });
  facts.push({ label: 'Rows staged for this currency', value: num('staged_rows') });
  facts.push({ label: 'Rows excluded from commit', value: num('excluded_rows') });
  return facts;
}

export interface ResultingUnit {
  key: string;
  unit: string;
  unitId: string | null;
  currency: string;
  rows: number;
  month: string | null;
  ytd: string | null;
}

/** "Resulting figures": `figures.by_unit` of each current batch, with its currency. */
export function resultingUnits(reviews: readonly ActualsReview[]): ResultingUnit[] {
  const units: ResultingUnit[] = [];
  for (const review of reviews) {
    const currency = review.import?.currency_code ?? '—';
    const byUnit = (review.figures['by_unit'] ?? []) as {
      business_unit: { id: string | null; name: string };
      rows: number;
      month_amount: string | null;
      year_to_date_amount: string | null;
    }[];
    for (const row of byUnit) {
      units.push({
        key: `${review.batch.id}-${row.business_unit.id ?? row.business_unit.name}`,
        unit: row.business_unit.name,
        unitId: row.business_unit.id,
        currency,
        rows: row.rows,
        month: row.month_amount,
        ytd: row.year_to_date_amount,
      });
    }
  }
  return units.sort((a, b) => a.unit.localeCompare(b.unit));
}

/** The batch the review panel opens by default: the newest not discarded. */
export function defaultBatch(batches: readonly ActualsStepBatch[]): ActualsStepBatch | undefined {
  return batches.find((batch) => batch.state.code !== 'DISCARDED');
}
